import { useState } from "react";
import { Handle, Position } from "@xyflow/react";
import { FONT, T, PETROL } from "../theme";
import type { StratRow, Slide } from "../types";
import { SLIDE_COMPONENTS } from "./index";

export function TitleNode({ data }: { data: { title: string; tag: string; ref?: boolean } }) {
  return (
    <div style={{ fontFamily: FONT, display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 30, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em" }}>{data.title}</span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: data.ref ? "#2B5560" : "#9A8FA0",
          background: data.ref ? "rgba(43,85,96,0.1)" : "rgba(154,143,160,0.14)",
          borderRadius: 999,
          padding: "4px 10px",
        }}
      >
        {data.tag}
      </span>
    </div>
  );
}

export function SectionTitleNode({ data }: { data: { title: string; sub?: string; band?: boolean } }) {
  if (data.band) {
    return (
      <div style={{ fontFamily: FONT }}>
        <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "0.22em", color: "#9A8FA0", marginBottom: 14 }}>SECTION</div>
        <div style={{ fontSize: 240, fontWeight: 800, lineHeight: 0.95, letterSpacing: "-0.035em", color: PETROL }}>{data.title}</div>
        {data.sub && <div style={{ marginTop: 22, fontSize: 46, fontWeight: 500, color: T.ink2, letterSpacing: "-0.01em" }}>{data.sub}</div>}
        <div style={{ marginTop: 30, width: 520, height: 5, background: PETROL, borderRadius: 3 }} />
      </div>
    );
  }
  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ fontSize: 110, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.03em", color: T.ink }}>{data.title}</div>
      {data.sub && <div style={{ marginTop: 16, fontSize: 30, fontWeight: 500, color: T.ink2, letterSpacing: "-0.01em" }}>{data.sub}</div>}
    </div>
  );
}

export function StratCardNode({ data }: { data: { label: string; title: string; lede?: string; rows?: StratRow[]; bullets?: string[] } }) {
  return (
    <div style={{ fontFamily: FONT, width: 780, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.10)", padding: 28 }}>
      <span style={{ display: "inline-block", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PETROL, background: "rgba(43,85,96,0.10)", borderRadius: 999, padding: "5px 12px", marginBottom: 14 }}>{data.label}</span>
      <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.15 }}>{data.title}</div>
      {data.lede && <div style={{ marginTop: 10, fontSize: 15, color: T.ink2, lineHeight: 1.5 }}>{data.lede}</div>}
      {data.rows && (
        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
          {data.rows.map((r) => (
            <div key={r.k} style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
              <span style={{ flexShrink: 0, width: 150, fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: T.ink }}>{r.k}</span>
              <span style={{ fontSize: 14, color: T.ink2, lineHeight: 1.45 }}>{r.v}</span>
            </div>
          ))}
        </div>
      )}
      {data.bullets && (
        <ul style={{ marginTop: 16, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 8 }}>
          {data.bullets.map((b, i) => <li key={i} style={{ fontSize: 14, color: T.ink2, lineHeight: 1.45 }}>{b}</li>)}
        </ul>
      )}
    </div>
  );
}

export function PainCardNode({ data }: { data: { n: string; problem: string; cause: string; solution: string } }) {
  return (
    <div style={{ fontFamily: FONT, width: 560, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.10)", padding: 26 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: PETROL, marginBottom: 12 }}>{data.n}</div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: "#B4552F", marginBottom: 6 }}>Problem</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.ink, lineHeight: 1.3, marginBottom: 18 }}>{data.problem}</div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: "#B8842B", marginBottom: 4 }}>Root cause</div>
      <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.45, marginBottom: 16 }}>{data.cause}</div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: PETROL, marginBottom: 4 }}>Solution</div>
      <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.45 }}>{data.solution}</div>
    </div>
  );
}

export const MAP_TONES: Record<string, { fg: string; bg: string }> = {
  petrol: { fg: "#2B5560", bg: "rgba(43,85,96,0.10)" },
  warm: { fg: "#B4552F", bg: "rgba(180,85,47,0.10)" },
  amber: { fg: "#B8842B", bg: "rgba(184,132,43,0.13)" },
  sage: { fg: "#5E7D5E", bg: "rgba(94,125,94,0.14)" },
  ink: { fg: "#57534E", bg: "#F0EBE3" },
};
export function MapCard({ data }: { data: { tag: string; tone: string; title: string; detail?: string; w?: number } }) {
  const t = MAP_TONES[data.tone] || MAP_TONES.ink;
  return (
    <div style={{ fontFamily: FONT, width: data.w || 320, background: T.white, border: `1px solid ${T.line}`, borderRadius: 14, boxShadow: "0 12px 34px rgba(0,0,0,0.09)", padding: 20 }}>
      <Handle type="target" position={Position.Top} style={{ opacity: 0, border: "none" }} />
      <span style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: t.fg, background: t.bg, borderRadius: 999, padding: "4px 10px", marginBottom: 10 }}>{data.tag}</span>
      <div style={{ fontSize: 16, fontWeight: 800, color: T.ink, letterSpacing: "-0.01em", lineHeight: 1.25 }}>{data.title}</div>
      {data.detail && <div style={{ marginTop: 7, fontSize: 13, color: T.ink2, lineHeight: 1.45 }}>{data.detail}</div>}
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, border: "none" }} />
    </div>
  );
}

export function PageSpecNode({ data }: { data: { tag: string; route: string; heroTitle: string; heroNote: string; subtitle: string; subNote: string; cta: string; sections: { label: string; heading: string; body: string }[] } }) {
  return (
    <div style={{ fontFamily: FONT, width: 460, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 16px 40px rgba(0,0,0,0.10)", padding: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#2B5560", background: "rgba(43,85,96,0.10)", borderRadius: 999, padding: "4px 10px" }}>{data.tag}</span>
        <span style={{ fontSize: 11, color: "#9A9188", fontFamily: "ui-monospace, monospace" }}>{data.route}</span>
      </div>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#B4552F", marginBottom: 4 }}>Hero title / what we do</div>
      <div style={{ fontSize: 23, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.15 }}>{data.heroTitle}</div>
      <div style={{ marginTop: 5, fontSize: 12.5, fontStyle: "italic", color: "#9A9188", lineHeight: 1.4 }}>{data.heroNote}</div>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#B8842B", marginTop: 16, marginBottom: 4 }}>Subtitle / introduces + explains the product</div>
      <div style={{ fontSize: 15, color: T.ink2, lineHeight: 1.5 }}>{data.subtitle}</div>
      <div style={{ marginTop: 4, fontSize: 12, fontStyle: "italic", color: "#9A9188" }}>{data.subNote}</div>
      <div style={{ marginTop: 14, display: "inline-block", background: "#1C2021", color: "#fff", fontSize: 13, fontWeight: 600, borderRadius: 999, padding: "9px 18px" }}>{data.cta}</div>
      <div style={{ height: 1, background: T.line, margin: "20px 0" }} />
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#2B5560", marginBottom: 8 }}>The content / page sections</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {data.sections.map((sct, i) => (
          <div key={i}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8A7F72", marginBottom: 3 }}>{sct.label}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>{sct.heading}</div>
            <div style={{ marginTop: 3, fontSize: 13, color: T.ink2, lineHeight: 1.45 }}>{sct.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CompMatrixNode({ data }: { data: { label: string; title: string; note?: string; headers: string[]; widths: number[]; rows: string[][] } }) {
  const W = data.widths.reduce((a, b) => a + b, 0);
  return (
    <div style={{ fontFamily: FONT, width: W + 56, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.10)", padding: 28 }}>
      <span style={{ display: "inline-block", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PETROL, background: "rgba(43,85,96,0.10)", borderRadius: 999, padding: "5px 12px", marginBottom: 12 }}>{data.label}</span>
      <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.15 }}>{data.title}</div>
      {data.note && <div style={{ marginTop: 8, fontSize: 13.5, color: T.ink2, lineHeight: 1.45, maxWidth: W - 200 }}>{data.note}</div>}
      <table style={{ marginTop: 18, borderCollapse: "collapse", tableLayout: "fixed", width: W }}>
        <colgroup>{data.widths.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
        <thead><tr>{data.headers.map((h, i) => (<th key={i} style={{ textAlign: "left", verticalAlign: "bottom", padding: "8px 10px", borderBottom: `2px solid ${T.line}`, color: PETROL, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1.25 }}>{h}</th>))}</tr></thead>
        <tbody>{data.rows.map((r, ri) => (<tr key={ri}>{r.map((c, ci) => (<td key={ci} style={{ textAlign: "left", verticalAlign: "top", padding: "11px 10px", borderBottom: `1px solid ${T.line}`, color: ci === 0 ? T.ink : T.ink2, fontWeight: ci === 0 ? 700 : 400, fontSize: ci === 0 ? 13.5 : 12.5, lineHeight: 1.42 }}>{c}</td>))}</tr>))}</tbody>
      </table>
    </div>
  );
}

export function SectionNode({ data }: { data: { eyebrow?: string; title: string; description?: string; w: number; slides: Slide[] } }) {
  const [i, setI] = useState(0);
  const n = data.slides.length;
  const idx = Math.min(i, n - 1);
  const active = data.slides[idx];
  const Child = SLIDE_COMPONENTS[active.type];
  const go = (d: number) => setI((p) => ((Math.min(p, n - 1)) + d + n) % n);
  const arrow: React.CSSProperties = { cursor: "pointer", fontFamily: FONT, width: 34, height: 34, borderRadius: "50%", border: `1px solid ${T.line}`, background: T.white, color: T.ink, fontSize: 18, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 };
  return (
    <div style={{ fontFamily: FONT, width: data.w }}>
      {data.eyebrow && <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: PETROL, marginBottom: 10 }}>{data.eyebrow}</div>}
      <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: "-0.03em", color: T.ink, lineHeight: 1.04 }}>{data.title}</div>
      {data.description && <div style={{ marginTop: 14, fontSize: 18, color: T.ink2, lineHeight: 1.5, maxWidth: 760 }}>{data.description}</div>}
      {/* tab strip + counter/arrows — nopan so interacting never pans the board; pointerEvents:auto
          re-enables hit-testing (RF sets pointer-events:none on non-draggable/non-selectable nodes). */}
      <div className="nopan nodrag" style={{ pointerEvents: "auto", display: "flex", alignItems: "center", gap: 12, margin: "26px 0 20px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
          {data.slides.map((s, k) => (
            <button
              key={k}
              onClick={() => setI(k)}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ cursor: "pointer", fontFamily: FONT, fontSize: 13.5, fontWeight: 600, padding: "9px 16px", borderRadius: 999, whiteSpace: "nowrap", color: k === idx ? T.white : T.ink2, background: k === idx ? PETROL : T.white, border: `1px solid ${k === idx ? PETROL : T.line}` }}
            >
              {s.tab}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.ink2, fontVariantNumeric: "tabular-nums" }}>{idx + 1} / {n}</span>
          <button className="nopan" onClick={() => go(-1)} onPointerDown={(e) => e.stopPropagation()} style={arrow}>‹</button>
          <button className="nopan" onClick={() => go(1)} onPointerDown={(e) => e.stopPropagation()} style={arrow}>›</button>
        </div>
      </div>
      {/* the active child card */}
      <div>{Child ? <Child data={active.data} /> : null}</div>
    </div>
  );
}

